import React, { useEffect, useRef, useState } from "react";
import { HqBranchesWrapper } from "./styled";
import {
  Box,
  ButtonStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { hqBranchTileArray } from "../../../helpers/adminConstants/AdminConstants";
import CustomTile from "../../../components/common/statusTiles/CustomTile";
import { Col, Row } from "react-bootstrap";
import ListTable from "../../../components/common/tables/ListTable";
import HistoryModal from "../../../components/admin/Modals/HistoryModal/HistoryModal";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { hqBranchColumns } from "../../../helpers/adminConstants/TableConstants";
import {
  departmentsHeadings,
  hqBranchesHeadings,
} from "../../../helpers/dummyConstants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileIcon3,
  PlaceholderImg,
  PlusIcon,
} from "../../../components/styled/AllImages";
import { useUser } from "../../../context/userContext";
import { toast } from "react-toastify";
import HQBranchesApi from "../../../services/api/hqBranchesApi";
import { getFormattedDate, getFormattedTime } from "../../../utils";
import { pdf } from "@react-pdf/renderer";
import { PdfDocumentTemplate } from "./components/PdfDocumentTemplate";
import { useDispatch } from "react-redux";
import {
  setBranchesDeps,
  setHqBranchesList,
} from "../../../store/slices/helperSlice";

const ASSET_URL = process.env.REACT_APP_ASSET_URL;

const HQBranches = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const dispatch = useDispatch();

  const [hqBranches, setHqBranches] = useState([]);
  const [hqBranchHistory, setHqBranchHistory] = useState({});
  const [hqBranchId, setHqBranchId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const iframeRef = useRef(null);

  const onOpen = (row) => {
    navigate(`/org-structure/departments?org-structure=${row.id}`);
  };
  const onDelete = (row) => {
    setHqBranchId(row.id);
    setShowDelete(true);
  };

  const onHistory = (row) => {
    getHqBranchHistory(row.id);
    setShowHistory(true);
  };

  const handleView = (row) => {
    navigate(`/org-structure/view-hq-branch/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/org-structure/edit-hq-branch/${row.id}`);
  };

  const handleDelete = async () => {
    try {
      const response = await HQBranchesApi.deleteHqBranch(hqBranchId);
      if (response.code === 200) {
        toast.success(t("hqbranch_deleted"));
        setShowDelete(false);
        setHqBranchId(null);
        getHqBranches();
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
      if (response.code === 204 || response.code === 100) {
        toast.error(response.message);
      }
    } catch (error) {}
  };
  const handleStatusToggle = async (row) => {
    const newObj = {
      _method: "PUT",
      active: parseInt(row.status) === 1 ? 0 : 1,
    };
    try {
      const response = await HQBranchesApi.changeHqBranchStatus(row.id, newObj);
      if (response.code === 200) {
        toast.success(t("hqbranch_status_updated"));
        getHqBranches();
      } else {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  const handleAddHQ = () => {
    if (user?.current_organization?.organization_name)
      navigate("/org-structure/add-hq-branch");
    else toast.error(t("please_complete_establishment"));
  };

  const getBranchesDepartments = async () => {
    try {
      const { data, code } = await HQBranchesApi.getBranchesDepartments();

      if (code === 200) {
        const branchesDepsArr = data?.map((item) => ({
          id: item?.id,
          name: item?.name,
          subLabel: item?.location?.name,
          location_id: item?.location_id,
        }));

        dispatch(setBranchesDeps(branchesDepsArr));
      }
    } catch (error) {}
  };

  const getHqBranchesWithoutPg = async () => {
    try {
      const { data, code } = await HQBranchesApi.getHqBranchesListWithoutPg();
      if (code === 200) {
        dispatch(setHqBranchesList(data));
      }
    } catch (error) {}
  };

  const getHqBranches = async () => {
    try {
      setLoading(true);
      const response = await HQBranchesApi.getHqBranches(activePage);
      if (response.code === 200) {
        getHqBranchesWithoutPg();
        getBranchesDepartments();
        const resData = response?.data?.list?.data.map((item) => ({
          ...item,
          no_of_users: item.users_detail_count,
          status: item.active,
          name: language === "ar" ? item.name_ar : item.name_en,
          type: item.location_type == 1 ? t("headquarter") : t("branch"),
          city: language === "en" ? item?.city?.en_name : item?.city?.ar_name,
        }));
        hqBranchTileArray[0].count = response?.data?.stats?.branch_count;
        hqBranchTileArray[1].count = response?.data?.stats?.headquarter_count;
        setHqBranches(resData);
        setTotalCount(response?.data?.list.total);
        setPerPage(response?.data?.list.per_page);
      } else {
        setHqBranches([]);
        setTotalCount(null);
        setPerPage(null);
        hqBranchTileArray[0].count = 0;
        hqBranchTileArray[1].count = 0;
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getHqBranchHistory = async (id) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await HQBranchesApi.getHqBranchHistory(id);
      if (code === 200) {
        const newCreatedBy = {
          id: data.id,
          name: data.created_by_name,
          subLabel: data?.department && data?.branchHQ ? `${data?.department} (${data?.branchHQ})` : "",
          avatar: data?.created_by_profile
            ? `${ASSET_URL}/${data?.created_by_profile}`
            : PlaceholderImg,
          date: getFormattedDate(data.created_at),
          time: getFormattedTime(data.created_at),
        };
        const editHistory =
          data?.update_history?.length > 0 &&
          data?.update_history?.map((user) => ({
            id: user.id,
            name: user.update_by_name,
            subLabel: user?.update_by_detail ? `${
              language === "en"
                ? user?.update_by_detail?.department_selective?.name_en
                : user?.update_by_detail?.department_selective?.name_ar
            } (${
              language === "en"
                ? user?.update_by_detail?.office_location_selective?.name_en
                : user?.update_by_detail?.office_location_selective?.name_ar
            })` : "",
            avatar: user?.update_by_profile
              ? `${ASSET_URL}/${user?.update_by_profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.updated_at),
            time: getFormattedTime(user.updated_at),
          }));

        setHqBranchHistory((prevData) => ({
          ...prevData,
          createdBy: newCreatedBy,
          editHistory: editHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  // const PdfPreviewButton = () => {
  //   const handleOpenPdf = async () => {
  //     const blob = await pdf(<HQBranchesPdfDocument />).toBlob();
  //     const url = URL.createObjectURL(blob);
  //     window.open(url, "_blank");
  //   }
  //   };

  const handlePrint = async () => {
    try {
      const { data, code } = await HQBranchesApi.getHqBranchesListWithoutPg();

      if (code === 200) {
        const resData = data?.map((item) => ({
          ...item,
          no_of_users: item.no_of_users,
          type_en: item.location_type == 1 ? "Headquarter" : "Branch",
          type_ar: item.location_type == 1 ? "المقر الرئيسي" : "فرع",
        }));

        const blob = await pdf(
          <PdfDocumentTemplate
            user={user}
            hqBranchesList={resData}
            hqBranchesHeadings={hqBranchesHeadings}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);

        if (window.matchMedia("(max-width: 768px)").matches) {
          const a = document.createElement("a");
          a.href = url;
          a.download = "org-structure.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          const iframe = iframeRef.current;
          if (iframe) {
            iframe.src = url;
            iframe.onload = () => {
              iframe.contentWindow.print();
            };
          }
        }
      }
    } catch (error) {
      toast.error(error?.message);
    }
  };

  useEffect(() => {
    getHqBranches();
  }, [language, activePage]);

  return (
    <HqBranchesWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="18px" weight="700" color="var(--text)">
          {t("org_structure")}
        </SubHeadingStyled>

        <Box className="d-flex align-items-center gap-2 head-item-two">
          <ButtonStyled
            onClick={handlePrint}
            label={t("print_as_pdf")}
            icon={FileIcon3}
            bg="var(--themeColor2)"
          />

          <iframe
            ref={iframeRef}
            style={{ display: "none" }}
            title="PDF Print Frame"
          />

          <ButtonStyled
            label={t("add_hq_branch")}
            icon={PlusIcon}
            onClick={handleAddHQ}
          />
        </Box>
      </Box>

      <Row className="mx-2 justify-content-center">
        {hqBranchTileArray.map((tile, key) => (
          <Col lg={3} md={6} sm={12} className="my-2">
            <CustomTile key={key} {...tile} />
          </Col>
        ))}
      </Row>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["open", "view", "info", "edit", "status", "delete"]}
            rows={hqBranches}
            columns={hqBranchColumns}
            onDelete={onDelete}
            onInfoView={onHistory}
            onViewDetails={handleView}
            onEdit={handleEdit}
            onOpen={onOpen}
            onStatusToggle={handleStatusToggle}
            totalCount={totalCount}
            perPage={perPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        )}
      </Box>
      <HistoryModal
        title={t("org_structure_history")}
        show={showHistory}
        onHide={() => setShowHistory(false)}
        createdBy={hqBranchHistory.createdBy}
        editHistory={hqBranchHistory.editHistory}
        historyLoading={historyLoading}
      />
      <DeleteModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        title={t("hq_branch")}
        onDelete={handleDelete}
      />
    </HqBranchesWrapper>
  );
};

export default HQBranches;
